/*
 * Copyright © 2023 Medaica, Inc
 *
 * All rights reserved.
 *
 * This code is confidential and proprietary information belonging to Medaica, Inc.
 * Unauthorized copying, distribution, or use of this code, in whole or in part,
 * is strictly prohibited, and may constitute a violation of intellectual property rights.
 *
 * If you have received this code in error, please notify the owner immediately
 * at support@medaica.com and delete this file from your system.
 */

import { autorun, makeObservable, observable, runInAction } from "mobx"
import { createLocalAudioTrack } from "twilio-video"
import { deviceIsM1, M1 } from "@medaica/common/services/m1"
import MediaDeviceStore, { AudioDevice, MediaDevice } from "@medaica/common/services/media-device-store"
import M1DeviceInfoUtil from "@medaica/common/services/m1-device-info-util"
import logger from "@medaica/common/services/logging"
import AVStore from "@medaica/common/views/exam/virtual-exam/stores/av-store"
import { patientVideoCaptureSize } from "@medaica/common/const"

// During preview the M1 is opened a second time with browser echo cancellation enabled,
// and that capture feeds only the m1RoomMicAudio voice track. The raw capture keeps
// feeding the auscultation track, monitors and the WAV recorder, so the heart-sound path is
// untouched. Set to false to compare against current production behavior.
const previewVoiceAecEnabled = true

class PatientVirtualExamMediaDeviceStore extends MediaDeviceStore {
  // If true, when the stethoscope is plugged in we will automatically connect to it, otherwise we won't.
  private readonly _connectToM1IfAvailable
  // This is the audio device which was connected before the M1 was connected to, so that when we disconnect the M1,
  // we know which device to reconnect to
  //private _fallbackAudioDeviceId?: string
  isM1Connected = false
  auscultationTrack: MediaStreamTrack
  private _m1DeviceInfoUtil: M1DeviceInfoUtil
  // Bumped whenever the audio device changes or the store is disposed, so an M1 connection that is still
  // waiting on getUserMedia can tell it has been superseded and release its captures instead of installing itself.
  private _m1ConnectionGeneration = 0
  // The conversation device released for a Preview voice capture, kept on the store so that any later failed
  // connection attempt — not only the one that released it — can reconnect it. Cleared when a device installs
  // or the store is disposed.
  private _pendingAudioDeviceRestore: { deviceId: string; wasM1: boolean } | null = null

  constructor(
    automaticallyActivateM1IfAvailable: boolean,
    m1DeviceInfoUtil: M1DeviceInfoUtil,
    preferredAudioDeviceId?: string | null,
    preferredVideoDeviceId?: string | null
  ) {
    super(preferredAudioDeviceId, preferredVideoDeviceId, patientVideoCaptureSize)
    this._m1DeviceInfoUtil = m1DeviceInfoUtil
    makeObservable(this, {
      isM1Connected: observable,
    })
    // todo we're not doing anything with this
    this._connectToM1IfAvailable = automaticallyActivateM1IfAvailable

    // We listen for the M1 to be plugged in
    autorun(() => {
      this.isM1Connected = this._m1DeviceInfoUtil.deviceInfosIncludeM1(this.availableAudioDevices)
    })
  }

  set m1DeviceInfoUtil(m1DeviceInfoUtil: M1DeviceInfoUtil) {
    this._m1DeviceInfoUtil = m1DeviceInfoUtil
    const m1IsAvailable = this._m1DeviceInfoUtil.deviceInfosIncludeM1(this.availableAudioDevices)
    runInAction(() => {
      this.isM1Connected = m1IsAvailable
    })
  }

  // todo get rid of this
  public async disconnectFromM1ForRecording(): Promise<void> {
    await this.connectToAudioDevice()
  }

  get m1Info(): { deviceId: string; label: string } | null {
    const m1MediaDeviceInfo = this._m1DeviceInfoUtil.findM1MediaDeviceInfo(this.availableAudioDevices)
    return m1MediaDeviceInfo
      ? {
          deviceId: m1MediaDeviceInfo.deviceId,
          label: m1MediaDeviceInfo.label,
        }
      : null
  }

  public async connectToM1(withAuscultationMicActive: boolean): Promise<M1 | null> {
    const m1MediaDeviceInfo = this._m1DeviceInfoUtil.findM1MediaDeviceInfo(this.availableAudioDevices)
    if (!m1MediaDeviceInfo) {
      throw new Error("M1 is not connected")
    }
    const constraints = {
      audio: {
        deviceId: { exact: m1MediaDeviceInfo.deviceId },
        channelCount: 2,
        ...(withAuscultationMicActive && {
          echoCancellation: false,
          autoGainControl: false,
          noiseSuppression: false,
        }),
      },
    }
    const generation = ++this._m1ConnectionGeneration
    const acquiresVoiceCapture = withAuscultationMicActive && previewVoiceAecEnabled
    let mediaStream: MediaStream | null = null
    let voiceStream: MediaStream | null = null
    let m1: M1 | null = null
    try {
      mediaStream = await navigator.mediaDevices.getUserMedia(constraints)
      logger.debug("M1 raw capture settings", mediaStream.getAudioTracks()[0]?.getSettings())
      if (generation !== this._m1ConnectionGeneration) {
        this.stopStream(mediaStream)
        return null
      }
      const currentAudioDevice = this.audioDevice
      if (
        acquiresVoiceCapture &&
        currentAudioDevice &&
        this.deviceUsesM1Hardware(currentAudioDevice, m1MediaDeviceInfo)
      ) {
        // While the conversation microphone holds an open processed capture of the M1, Chromium pins the device's
        // audio-processing settings and the echo-cancelled voice capture below comes back unprocessed. Release it
        // first; the M1 connected below replaces it, and a failure restores it.
        logger.debug("Conversation microphone uses the M1, releasing it before the voice capture")
        this._pendingAudioDeviceRestore = {
          deviceId: currentAudioDevice.id,
          wasM1: deviceIsM1(currentAudioDevice),
        }
        runInAction(() => {
          this.setAudioDevice(null)
        })
      }
      voiceStream = acquiresVoiceCapture ? await this.acquireVoiceStream(m1MediaDeviceInfo.deviceId) : null
      if (generation !== this._m1ConnectionGeneration) {
        this.stopStream(mediaStream)
        if (voiceStream) {
          this.stopStream(voiceStream)
        }
        return null
      }
      const connectedM1 = new M1(mediaStream, m1MediaDeviceInfo.deviceId, m1MediaDeviceInfo.label, true, voiceStream)
      m1 = connectedM1
      logger.debug("connecting to M1, recording mode", withAuscultationMicActive)
      runInAction(() => {
        this.setAudioDevice(connectedM1)
      })
      return connectedM1
    } catch (error) {
      logger.error("Error while connecting to M1:", error)
      // Release whatever was captured before the failure, so the M1 is not left open
      if (m1) {
        m1.dispose()
      } else {
        if (mediaStream) {
          this.stopStream(mediaStream)
        }
        if (voiceStream) {
          this.stopStream(voiceStream)
        }
      }
      this.isM1Connected = false
      const pendingRestore = this._pendingAudioDeviceRestore
      if (pendingRestore && generation === this._m1ConnectionGeneration && !this.audioDevice) {
        await this.restoreReleasedAudioDevice(pendingRestore)
      }
      return null
    }
  }

  /**
   * True when the conversation device captures the same physical hardware as the M1: directly (its id is the M1's),
   * or through an alias such as "default", which the live track's groupId ties to the physical device.
   */
  private deviceUsesM1Hardware(device: AudioDevice, m1Info: MediaDeviceInfo): boolean {
    if (device.id === m1Info.deviceId) {
      return true
    }
    const settings = device.track?.mediaStreamTrack.getSettings()
    return Boolean(m1Info.groupId) && settings?.groupId === m1Info.groupId
  }

  /**
   * Reconnects the conversation microphone that connectToM1 released before the voice capture, for when M1 setup
   * fails afterwards. The pending state is consumed up front so a nested failure cannot restore twice, and the
   * connection generation guards each branch, so a restore that resolves after a newer connection request or
   * disposal neither installs a stale device nor leaves its track running. The microphone comes back as the same
   * kind of capture it was: an M1 wrapper is rebuilt only when an M1 wrapper was released (and the M1 is still
   * enumerated), everything else — including an ordinary capture of the M1 hardware — returns as an ordinary
   * conversation capture.
   */
  private async restoreReleasedAudioDevice(restore: { deviceId: string; wasM1: boolean }): Promise<void> {
    // Consumed up front so a nested M1 rebuild failure cannot restore recursively. Known limitation: a newer
    // connection attempt that starts during this restore and also fails finds nothing left to restore, leaving
    // the store deviceless until the next device selection. The clean fix is serializing device transitions.
    this._pendingAudioDeviceRestore = null
    logger.debug("Restoring the conversation microphone released for the M1 voice capture")
    const deviceInfo = this.availableAudioDevices.find((info) => info.deviceId === restore.deviceId)
    if (restore.wasM1 && deviceInfo && this._m1DeviceInfoUtil.isM1DeviceInfo(deviceInfo)) {
      // connectToM1 carries its own generation guard: it bumps the generation exactly once, synchronously,
      // so after the await anything beyond one bump means disposal or a newer connection superseded the rebuild
      const generationBeforeRebuild = this._m1ConnectionGeneration
      const m1 = await this.connectToM1(false)
      if (m1) {
        // Unlike activateAuscultationMic, this path has no caller that silences the M1 on the local speakers
        m1.disableAuscultationMicOnLocalSpeakers()
        m1.disableRoomMicOnLocalSpeakers()
        this.recomputeIsM1Connected()
        return
      }
      if (this._m1ConnectionGeneration !== generationBeforeRebuild + 1 || this.audioDevice) {
        // The rebuild was superseded, not failed: the microphone now belongs to the newer request or to nobody
        // (disposal), so opening a fallback capture here would reopen or replace it
        return
      }
      // The M1 could not be rebuilt; fall through and restore an ordinary capture instead
    }
    const generation = ++this._m1ConnectionGeneration
    try {
      const localAudioTrack = await createLocalAudioTrack({
        name: AVStore.genTrackName("defaultAudio"),
        echoCancellation: true,
        autoGainControl: true,
        noiseSuppression: true,
        ...(deviceInfo && { deviceId: restore.deviceId }),
      })
      if (generation !== this._m1ConnectionGeneration) {
        localAudioTrack.stop()
        return
      }
      const audioDevice = new MediaDevice(localAudioTrack)
      audioDevice.setMuted(this.audioDeviceIsMuted)
      runInAction(() => {
        this.setAudioDevice(audioDevice)
      })
      // The failed M1 attempt set isM1Connected to false; the device list knows whether the M1 is still there
      this.recomputeIsM1Connected()
    } catch (error) {
      logger.error("Could not restore the conversation microphone after M1 setup failed:", error)
    }
  }

  private recomputeIsM1Connected(): void {
    const m1IsAvailable = this._m1DeviceInfoUtil.deviceInfosIncludeM1(this.availableAudioDevices)
    runInAction(() => {
      this.isM1Connected = m1IsAvailable
    })
  }

  // Any successful installation supersedes a pending restore of the released conversation microphone
  override setAudioDevice(audioDevice: AudioDevice | null): void {
    if (audioDevice) {
      this._pendingAudioDeviceRestore = null
    }
    super.setAudioDevice(audioDevice)
  }

  /**
   * Opens the M1 a second time with echo cancellation and noise suppression, for the voice path only. Auto gain
   * stays off: Chromium's AGC can move the device's input level, which the raw recording capture shares.
   * Returns null when the second capture fails, the browser could not enable echo cancellation on it, or the
   * browser left auto gain on anyway (Edge on Windows does), in which case the caller falls back to current
   * behavior (voice from the raw capture).
   */
  private async acquireVoiceStream(deviceId: string): Promise<MediaStream | null> {
    try {
      const voiceStream = await navigator.mediaDevices.getUserMedia({
        audio: {
          deviceId: { exact: deviceId },
          channelCount: 2,
          echoCancellation: true,
          // Mandatory: a browser that cannot keep auto gain off must reject the call instead of opening the device
          autoGainControl: { exact: false },
          noiseSuppression: true,
        },
      })
      const settings = voiceStream.getAudioTracks()[0]?.getSettings()
      logger.debug("M1 voice (AEC) capture settings", settings)
      // echoCancellation: true is only a preference, so the browser may still hand back an uncancelled stream
      if (settings?.echoCancellation !== true) {
        this.stopStream(voiceStream)
        logger.warn("Preview voice capture has no echo cancellation, falling back to raw voice path")
        return null
      }
      // Auto gain on this capture could move the device input level the raw recording capture shares
      if (settings.autoGainControl !== false) {
        this.stopStream(voiceStream)
        logger.warn("Preview voice capture has auto gain control on, falling back to raw voice path")
        return null
      }
      return voiceStream
    } catch (error) {
      logger.error("Preview voice AEC capture failed, falling back to raw voice path:", error)
      return null
    }
  }

  private stopStream(stream: MediaStream): void {
    stream.getTracks().forEach((track) => track.stop())
  }

  private isAudioDeviceAvailable(deviceId?: string | null): deviceId is string {
    if (deviceId === null) {
      return false
    }
    return this.availableAudioDevices.some((deviceInfo) => deviceInfo.deviceId === deviceId)
  }

  private async connectToStandardAudioDeviceOrM1(audioDeviceId: string) {
    const deviceInfo = this.availableAudioDevices.find((device) => device.deviceId === audioDeviceId)
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    if (this._m1DeviceInfoUtil.isM1DeviceInfo(deviceInfo!)) {
      await this.connectToM1(false)
    } else {
      await super.connectToAudioDevice(audioDeviceId)
    }
  }

  async connectToDefaultAudioDevice(): Promise<void> {
    const generation = ++this._m1ConnectionGeneration
    await super.connectToAudioDevice()
    await this.restorePendingAudioDeviceAfterFailedConnect(generation)
  }

  override dispose(): void {
    this._m1ConnectionGeneration++
    this._pendingAudioDeviceRestore = null
    super.dispose()
  }

  override async connectToAudioDevice(audioDeviceId?: string | null): Promise<void> {
    const generation = ++this._m1ConnectionGeneration
    if (this.isAudioDeviceAvailable(audioDeviceId)) {
      logger.debug("Connecting to audio device", audioDeviceId)
      await this.connectToStandardAudioDeviceOrM1(audioDeviceId)
    } else {
      // If we get here it means we haven't connected to a fallback or selected device, so we'll just connect to the
      // default device
      logger.debug("Connecting to default audio device")
      await super.connectToAudioDevice()
    }
    await this.restorePendingAudioDeviceAfterFailedConnect(generation)
  }

  /**
   * The base connectToAudioDevice swallows its errors, so a reconnect that fails while a microphone released for
   * the Preview voice capture is awaiting restore would otherwise leave the patient with no conversation audio.
   * Only the newest connection attempt restores; an attempt superseded by a newer one leaves the device to it.
   */
  private async restorePendingAudioDeviceAfterFailedConnect(generation: number): Promise<void> {
    const pendingRestore = this._pendingAudioDeviceRestore
    if (pendingRestore && generation === this._m1ConnectionGeneration && !this.audioDevice) {
      await this.restoreReleasedAudioDevice(pendingRestore)
    }
  }
}

export default PatientVirtualExamMediaDeviceStore
